import React, { useState } from 'react';

import { ContextGlobal as CG } from 'contexts';
import { useConversionOptions, useUser } from 'hooks';

import * as Form from 'components/form';
import * as Page from 'components/page';
import * as Alert from 'components/alert';
import { isValidEmail } from 'utils/validation';
import { CorporationCard } from '../components/corporation/CorporationCard';
import * as Styled from './StyledEcosystemStep3AddForm';

export function EcosystemStep3AddForm(props) {
    const user = useUser();
    const { form, onSubmit, language } = props;
    const { getCompanyByDomain, getCompanyDetails } = props.useRankingEcosystem;

    const [renderWarning, setRenderWarning] = useState(false);
    const { emitAlert } = React.useContext(CG);

    const [isEmailValid, setIsEmailValid] = useState(false);
    const [validCompany, setValidCompany] = useState(true);
    const [validDomain, setValidDomain] = useState(true);
    const [smallCompany, setSmallCompany] = useState(false);
    const [showEmailLanguageField, setShowEmailLanguageField] = useState(false);

    const [searchedCompany, setSearchedCompany] = useState(undefined);

    const convEmailLanguage = useConversionOptions(['language']);
    const emailLanguageOptions = [
        ...convEmailLanguage.language.filter(type => type.value !== 'none'),
    ];

    function checkIfSmallCompany(res) {
        try {
            setSmallCompany(false);

            if (res.company.id === undefined) {
                return;
            }
            getCompanyDetails(res.company.id).then(res => {
                const { companyType } = res;
                if (companyType === 3 || undefined) {
                    setSmallCompany(true);
                    //const errorMessage = props.t('StartupStep8Add.smallCompanyError');
                    //emitAlert(errorMessage);
                } else {
                    setSearchedCompany(res);
                }
            });
        } catch (err) {}
    }

    // Create form fields object
    const fields = {
        companyContactName: {
            error: form.errors.companyContactName,
            label: props.t('companyContactName'),
            name: 'companyContactName',
            type: 'text',
            value: form.values.companyContactName,
            onChange: form.handleChange,
        },
        companyContactEmail: {
            error: form.errors.companyContactEmail,
            label: props.t('companyContactEmail'),
            name: 'companyContactEmail',
            type: 'email',
            value: form.values.companyContactEmail,
            onBlur: ev => {
                const domain = (ev.target.value || '').split('@')[1];

                if (!domain || !isValidEmail(ev.target.value)) {
                    return setSearchedCompany(undefined);
                }
                getCompanyByDomain(domain).then(res => {
                    setSearchedCompany(undefined);
                    checkIfSmallCompany(res);
                    if (res.validdomain) {
                        setValidCompany(res.validcompany);
                        setValidDomain(true);
                        return;
                    }
                    if (!res.validdomain) {
                        const errorMessage =
                            'O endereço de email do contato não pode ter domínio público.';
                        emitAlert(errorMessage);
                    }
                    setValidCompany(true);
                    setValidDomain(false);
                });
            },
            onChange: form.handleChange,
        },

        companyConfirmationEmail: {
            error: form.errors.companyConfirmationEmail,
            label: props.t('companyContactEmail2'),
            name: 'companyConfirmationEmail',
            type: 'email',
            value: form.values.companyConfirmationEmail,
            onChange: form.handleChange,
        },

        companyName: {
            error: form.errors.companyName,
            label: props.t('glossary:companyName'),
            name: 'companyName',
            type: 'text',
            value: form.values.companyName,
            onChange: form.handleChange,
            required: !validCompany,
        },

        emailLanguage: {
            error: form.errors.emailLanguage,
            label: props.t('glossary:emailLanguage'),
            name: 'emailLanguage',
            type: 'select',
            options: emailLanguageOptions,
            required: showEmailLanguageField,
            value: form.values.emailLanguage,
            onChange: form.handleChange,
        },

        ecosystemCompanyName: {
            error: form.errors.ecosystemCompanyName,
            label: props.t('ecossystemCompanyName'),
            name: 'ecosystemCompanyName',
            type: 'text',
            value: form.values.ecossystemCompanyName,
            onChange: form.handleChange,
            readOnly: user?.company?.id,
        },
        programName: {
            error: form.errors.programName,
            label: props.t('glossary:programName'),
            name: 'programName',
            type: 'text',
            value: form.values.programName,
            onChange: form.handleChange,
        },
        description: {
            error: form.errors.description,
            label: props.t('glossary:description'),
            name: 'description',
            required: false,
            type: 'textarea',
            value: form.values.description,
            onChange: form.handleChange,
        },
        year: {
            displayFormat: 'YYYY',
            error: form.errors.year,
            label: props.t('glossary:year'),
            maxLength: 4,
            name: 'year',
            type: 'text',
            value: form.values.year,
            onChange: form.handleChange,
        },
        linkUrl: {
            error: form.errors.linkUrl,
            label: props.t('glossary:linkUrl'),
            name: 'linkUrl',
            type: 'text',
            required: false,
            value: form.values.linkUrl,
            onChange: form.handleChange,
        },
    };

    const companyContactEmailValue = fields.companyContactEmail.value;
    const companyConfirmationEmailValue = fields.companyConfirmationEmail.value;

    React.useEffect(() => {
        setIsEmailValid(companyConfirmationEmailValue === companyContactEmailValue);
    }, [isEmailValid, companyContactEmailValue, companyConfirmationEmailValue]);

    const propsAlert = {
        descr: props.t('glossary:emailConfirmationError'),
        icons: 'far fa-exclamation-circle',
        type: 'warning',
        color: 'red',
    };

    // Handle form submition
    const evSubmit = ev => {
        ev.persist();
        ev.preventDefault();

        form.values.emailLanguage = language;
        if (showEmailLanguageField) {
            form.values.emailLanguage = fields.emailLanguage.value;
        }

        if (!isEmailValid) {
            setRenderWarning(true);
            return false;
        }
        return form.handleSubmit(ev, onSubmit);
    };

    return (
        <Form.Form name="EcosystemStep3AddForm" onSubmit={evSubmit}>
            <Form.Field {...fields.companyContactName} />

            <Form.Field {...fields.companyContactEmail} />
            {!validDomain && (
                <Styled.FieldError className="domain-error">
                    {props.t('StartupStep8Add.domainError')}
                </Styled.FieldError>
            )}
            {smallCompany && (
                <Styled.FieldError className="domain-error">
                    {props.t('StartupStep8Add.smallCompanyError')}
                </Styled.FieldError>
            )}

            <Form.Field {...fields.companyConfirmationEmail} />
            {renderWarning && <Alert.AlertRow {...propsAlert} />}

            {!validCompany && <Form.Field {...fields.companyName} />}

            {searchedCompany && (
                <Page.Boxed style={{ marginTop: '2rem' }}>
                    {!!Object.keys(searchedCompany).length && (
                        <>
                            <CorporationCard
                                {...form.values}
                                data={searchedCompany}
                                useRankingEcosystem={props.useRankingEcosystem}
                            />
                        </>
                    )}
                    <Styled.LanguageText onClick={() => setShowEmailLanguageField(true)}>
                        {!showEmailLanguageField &&
                            props.t('CorporateStep4Add.emailLanguageInfo', {
                                startupLanguage: props.t(`conversionTable:language.${language}`),
                            })}
                    </Styled.LanguageText>

                    {showEmailLanguageField && <Form.Field {...fields.emailLanguage} />}
                </Page.Boxed>
            )}

            <Page.Ruler />

            <Form.Field {...fields.ecosystemCompanyName} />
            <Form.Field {...fields.programName} />
            <Form.Row>
                <Form.Field size="4" {...fields.year} />
                <Form.Field size="8" {...fields.linkUrl} />
            </Form.Row>
            <Form.Field {...fields.description} />

            <Form.Submt
                {...form}
                text={props.t('glossary:save')}
                disabled={!isEmailValid || !validDomain || smallCompany}
            />
        </Form.Form>
    );
}
export default EcosystemStep3AddForm;
